import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useParams } from 'react-router-dom'
import type {
  SourceGroupCreatePayload,
  SourceGroupListResponse,
  SourceGroupPatchPayload,
} from '@/features/installations/api/source-group-model'
import { installationPrecursorCnCodesQueryKey } from '@/features/installations/hooks/useInstallationConfiguration'
import { apiClient } from '@/shared/api/apiClient'
import { useSession } from '@/shared/hooks/useSession'

const sessionPath = (
  organizationId: string | undefined,
  installationId: string | undefined
) =>
  organizationId && installationId
    ? `/v2/organizations/${organizationId}/installations/${installationId}`
    : null

function useInstallationSourceGroupsContext() {
  const { installationId, precursorCnCodeId } = useParams<{
    installationId: string
    precursorCnCodeId: string
  }>()
  const {
    session: {
      organization: { id: organizationId },
    },
  } = useSession()
  const basePath = sessionPath(organizationId, installationId)
  const sourceGroupsPath =
    basePath && precursorCnCodeId
      ? `${basePath}/precursor-cn-codes/${precursorCnCodeId}/source-groups`
      : null

  return { organizationId, installationId, precursorCnCodeId, basePath, sourceGroupsPath }
}

export const installationSourceGroupsQueryKey = (
  organizationId: string | undefined,
  installationId: string | undefined,
  precursorCnCodeId: string | undefined
) =>
  ['installation-source-groups', organizationId, installationId, precursorCnCodeId] as const

/** Lists source groups for the current route's installation and precursor CN code. */
export const useInstallationSourceGroupsQuery = () => {
  const { organizationId, installationId, precursorCnCodeId, sourceGroupsPath } =
    useInstallationSourceGroupsContext()

  return useQuery({
    queryKey: installationSourceGroupsQueryKey(
      organizationId,
      installationId,
      precursorCnCodeId
    ),
    queryFn: async () => {
      const res = await apiClient.get<SourceGroupListResponse>(sourceGroupsPath!)
      return res.data.sourceGroups
    },
    enabled: Boolean(sourceGroupsPath),
  })
}

/** Creates, updates, and deletes source groups for the current route's installation and precursor CN code. */
export const useInstallationSourceGroupMutations = () => {
  const queryClient = useQueryClient()
  const { organizationId, installationId, precursorCnCodeId, sourceGroupsPath } =
    useInstallationSourceGroupsContext()
  const key = installationSourceGroupsQueryKey(
    organizationId,
    installationId,
    precursorCnCodeId
  )

  const invalidate = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: key }),
      queryClient.invalidateQueries({
        queryKey: installationPrecursorCnCodesQueryKey(organizationId, installationId),
      }),
    ])
  }

  const addSourceGroup = useMutation({
    mutationFn: async (payload: SourceGroupCreatePayload) => {
      await apiClient.post(sourceGroupsPath!, payload)
    },
    onSuccess: invalidate,
  })

  const patchSourceGroup = useMutation({
    mutationFn: async ({
      sourceGroupId,
      payload,
    }: {
      sourceGroupId: string
      payload: SourceGroupPatchPayload
    }) => {
      await apiClient.patch(`${sourceGroupsPath!}/${sourceGroupId}`, payload)
    },
    onSuccess: invalidate,
  })

  const removeSourceGroup = useMutation({
    mutationFn: async (sourceGroupId: string) => {
      await apiClient.delete(`${sourceGroupsPath!}/${sourceGroupId}`)
    },
    onSuccess: invalidate,
  })

  return { addSourceGroup, patchSourceGroup, removeSourceGroup }
}
